import React, { useEffect, useState } from 'react';
import { Formik } from 'formik';
import * as yup from 'yup';
import Select from 'react-select';
import { usePlacesWidget } from 'react-google-autocomplete';

const API_BASE_URL = 'https://tokenly.biswaas.com/api';

const validationSchema = yup.object().shape({
  clinic_name: yup.string().required('This field is required'),
});

const ClinicProfile = () => {
  const [isLoading, setLoading] = useState(false);
  const [specialtiesList, setSpecialtiesList] = useState([]);
  const [userData, setUserData] = useState({});
  const [doctorIdProfile, setDoctorIdProfile] = useState(null);

  const { ref: placesRef } = usePlacesWidget({
    apiKey: 'AIzaSyD7iJcig9sJfmUvHRCXNYJU1i3WGmc9cA4',
    onPlaceSelected: (place) => {
      console.log(place);
    },
    options: {
      types: ['address'],
      componentRestrictions: { country: 'in' },
    },
  });

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('tokenly_user') || '{}');
    let doctorId = user.doctorId || null;
    
    if (doctorId) {
      setDoctorIdProfile(doctorId);
    }
    
    getProfileApi(doctorId);
    getSpecialtiesList();
  }, []);

  const getProfileApi = async (doctorId) => {
    const token = localStorage.getItem('tokenly_token');
    if (!token) {
      console.error('No token found');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/user-detail`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ 
          page: 'docProfile', 
          doctorId: doctorId 
        }),
      });

      const data = await response.json();
      
      if (data.status) {
        const processedData = {
          ...data.data,
          specialties: data.data.specialties ? JSON.parse(data.data.specialties) : []
        };
        setUserData(processedData);
      } else {
        console.error('Failed to load profile:', data.message);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSpecialtiesList = async () => {
    const token = localStorage.getItem('tokenly_token');
    if (!token) {
      console.error('No token found');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/getTermsList`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ taxonomy_id: 39 }),
      });

      const data = await response.json();
      
      if (data.status) {
        setSpecialtiesList(data.data);
      } else {
        console.error('Failed to load specialties:', data.message);
      }
    } catch (error) {
      console.error('Error fetching specialties:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    const token = localStorage.getItem('tokenly_token');
    if (!token) {
      console.error('No token found');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/user/clinicProfileUpdate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          page: 'clinicProfile',
          ...values,
          specialties: JSON.stringify(values.specialties)
        }),
      });

      const data = await response.json();
      
      if (data.status) {
        alert('Profile updated successfully!');
        setUserData(data.data);
      } else {
        alert('Failed to update profile: ' + data.message);
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const specialtyOptions = specialtiesList.map(specialty => ({
    value: specialty.id,
    label: specialty.title
  }));

  const user = JSON.parse(localStorage.getItem('tokenly_user') || '{}');
  const roleId = user.roleId;

  return (
    <>
      {/* Hero Section with Background Image - Same as other pages */}
      <section className="contact-hero d-flex align-items-center">
        <div className="contact-hero-overlay"></div>
        <div className="container position-relative">
          <h1 className="display-5 fw-bold text-white mb-1">Clinic <span className="text-warning">Profile</span></h1>
          <p className="text-light mb-0">
            Manage your clinic information and settings
          </p>
        </div>
      </section>

      <div className="container-md py-5">
        <div className="row justify-content-center">
          <div className="col-12">
            <div className="card shadow-lg border-0 overflow-hidden" style={{
              borderRadius: "24px",
              backgroundColor: "#ffffff",
              boxShadow: '0 20px 50px rgba(0,0,0,0.12) !important'
            }}>
              <div className="card-header text-white" style={{
                background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                borderRadius: '24px 24px 0 0',
                border: 'none',
                padding: '1.5rem'
              }}>
                <h4 className="mb-0">Clinic Profile Information</h4>
              </div>
              <div className="card-body">
                {isLoading && (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                )}

                <Formik
                  enableReinitialize={true}
                  initialValues={{
                    page: 'clinicProfile',
                    id: userData.id || null,
                    is_clinic: userData.is_clinic || 0,
                    is_hide_list: userData.is_hide_list || 0,
                    specialties: userData.specialties || [],
                    clinic_name: userData.clinic_name || '',
                    clinic_phone: userData.clinic_phone || '',
                    street_address: userData.street_address || '',
                    mobile2: userData.mobile2 || '',
                    reg_num: userData.reg_num || '',
                    room_num: userData.room_num || '',
                    lat: userData.lat || '',
                    lng: userData.lng || '',
                  }}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                >
                  {({
                    handleChange,
                    handleBlur,
                    handleSubmit,
                    values,
                    errors,
                    touched,
                    setFieldValue,
                  }) => (
                    <form onSubmit={handleSubmit}>
                      {!userData.doctor_id && roleId === 3 && (
                        <div className="mb-3">
                          <div className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="manageClinicCheck"
                              checked={values.is_clinic === 1}
                              onChange={(e) => {
                                setFieldValue('is_clinic', e.target.checked ? 1 : 0);
                              }}
                            />
                            <label className="form-check-label" htmlFor="manageClinicCheck">
                              Do you manage multiple doctors in a clinic?
                            </label>
                          </div>
                        </div>
                      )}

                      {!userData.doctor_id && roleId === 3 && (
                        <div className="mb-3">
                          <label className="form-label">Clinic Address</label>
                          <input
                            ref={placesRef}
                            type="text"
                            className="form-control"
                            placeholder="Search for address"
                            onChange={(e) => {
                              const script = document.createElement('script');
                              script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyD7iJcig9sJfmUvHRCXNYJU1i3WGmc9cA4&libraries=places`;
                              script.async = true;
                              document.body.appendChild(script);
                            }}
                          />
                          {values.street_address && (
                            <small className="text-muted">{values.street_address}</small>
                          )}
                        </div>
                      )}

                      <div className="mb-3">
                        <label className="form-label">Specialties</label>
                        <Select
                          isMulti
                          options={specialtyOptions}
                          value={specialtyOptions.filter(option => 
                            values.specialties.includes(option.value)
                          )}
                          onChange={(selectedOptions) => {
                            const selectedValues = selectedOptions ? selectedOptions.map(option => option.value) : [];
                            setFieldValue('specialties', selectedValues);
                          }}
                          placeholder="Select Specialties..."
                          className="react-select-container"
                          classNamePrefix="react-select"
                        />
                      </div>

                      {!userData.doctor_id && roleId === 3 && (
                        <>
                          <div className="mb-3">
                            <label className="form-label">Clinic Name *</label>
                            <input
                              type="text"
                              name="clinic_name"
                              value={values.clinic_name}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              placeholder="Clinic Name"
                              className={`form-control ${
                                errors.clinic_name && touched.clinic_name ? 'is-invalid' : ''
                              }`}
                            />
                            {errors.clinic_name && touched.clinic_name && (
                              <div className="invalid-feedback">{errors.clinic_name}</div>
                            )}
                          </div>

                          <div className="mb-3">
                            <label className="form-label">Clinic Contact Number</label>
                            <input
                              type="tel"
                              name="clinic_phone"
                              value={values.clinic_phone}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              placeholder="Clinic Contact Number"
                              className="form-control"
                            />
                          </div>

                          <div className="mb-3">
                            <label className="form-label">Emergency Contact Number</label>
                            <input
                              type="tel"
                              name="mobile2"
                              value={values.mobile2}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              placeholder="Emergency Contact Number"
                              className="form-control"
                            />
                          </div>
                        </>
                      )}

                      <div className="mb-3">
                        <label className="form-label">Registration Number</label>
                        <input
                          type="text"
                          name="reg_num"
                          value={values.reg_num}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder="Registration Number"
                          className="form-control"
                        />
                      </div>

                      <div className="mb-3">
                        <label className="form-label">Room Number</label>
                        <input
                          type="text"
                          name="room_num"
                          value={values.room_num}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder="Room Number"
                          className="form-control"
                        />
                      </div>

                      {roleId === 3 && (
                        <div className="mb-3">
                          <div className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="hideListCheck"
                              checked={values.is_hide_list === 1}
                              onChange={(e) => {
                                setFieldValue('is_hide_list', e.target.checked ? 1 : 0);
                              }}
                            />
                            <label className="form-check-label" htmlFor="hideListCheck">
                              Hide public view?
                            </label>
                          </div>
                        </div>
                      )}

                      <div className="d-grid gap-2 d-md-flex justify-content-md-center">
                        <button
                          type="submit"
                          disabled={isLoading}
                          className="btn btn-primary btn-lg"
                        >
                          {isLoading ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              Submitting...
                            </>
                          ) : (
                            'Submit'
                          )}
                        </button>
                      </div>
                    </form>
                  )}
                </Formik>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ClinicProfile;